import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatSort } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  CollectionUtil,
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderNumberCellDirective,
  ElderQuantityPipe,
  ElderStopEventPropagationDirective,
  ElderTableActivationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  LocalListDataSource,
  Unit,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { Package } from '../../packages/package'
import { PackageFilterBuilder } from '../../packages/package-filter-builder'
import { PackageService } from '../../packages/package.service'
import { StoragePath } from '../../storage-path'
import { StorageGroup } from '../../storagegroups/storage-group'
import { PackageGroupRow } from './package-group-row'
import { PackageGroupRowBuilder } from './package-group-row.builder'

@Component({
  selector: 'app-storage-manager-packages',
  templateUrl: './storage-manager-packages.component.html',
  styleUrls: ['./storage-manager-packages.component.scss'],
  imports: [
    ElderTableComponent,
    ElderTableActivationDirective,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    ElderNumberCellDirective,
    MatIconButton,
    ElderStopEventPropagationDirective,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
  ],
})
export class StorageManagerPackagesComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly unitKg = Unit.kg

  public readonly storageGroup$ = new BehaviorSubject<StorageGroup>(null)

  public readonly rows$ = new BehaviorSubject<PackageGroupRow[]>([])
  public readonly activeItem$ = new BehaviorSubject<PackageGroupRow>(null)

  private readonly _dataSource: LocalListDataSource<PackageGroupRow> =
    LocalListDataSource.empty(null)
  public data: IDataContext<PackageGroupRow>

  private packageService = inject(PackageService)

  @Input()
  public storageScope: StoragePath

  @Output()
  public movePackages = new EventEmitter<Package[]>()

  @Output()
  public packageSelected = new EventEmitter<Package>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<PackageGroupRow>()
      .autoStartAsap()
      .reloadOnLocalChanges()
      .build(this._dataSource)

    this.rows$.pipe(takeUntilDestroyed()).subscribe({
      next: (rows) => {
        this._dataSource.data = rows ? rows : []
      },
    })
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set storageGroup(storageGroup: StorageGroup) {
    this.log.info('new storageGroup: ', storageGroup)
    this.storageGroup$.next(storageGroup)
    this.buildRowModel(storageGroup)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public canMove(pkgGroup: PackageNodeGroup): boolean {
    return pkgGroup?.packages ? pkgGroup.storagePath.isSubPathOfPath(this.storageScope) : false
  }

  public onMove(pkgGroup: PackageNodeGroup) {
    this.movePackages.emit(pkgGroup.packages)
  }

  public changeActiveItem(selectedItem: PackageGroupRow): void {
    if (selectedItem?.isChild) {
      this.activeItem$.next(selectedItem)
      this.packageSelected.emit(selectedItem.item)
    } else {
      this.packageSelected.emit(new Package())
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildRowModel(storageGroup: StorageGroup) {
    if (storageGroup) {
      this.loadPackagesNodeGroups([
        PackageFilterBuilder.storageGroupIds(storageGroup?.id),
      ]).subscribe((packageNodeGroups) => {
        const builtRows = PackageGroupRowBuilder.buildRows(packageNodeGroups)
        this.rows$.next(builtRows)
      })
    }
  }

  private loadPackagesNodeGroups(filters: Filter[]): Observable<PackageNodeGroup[]> {
    return this.packageService.listAll(filters).pipe(map((packages) => this.aggregate(packages)))
  }

  private aggregate(packages: Package[]): PackageNodeGroup[] {
    const assignedPackages = packages.filter((p) => p.location !== null)
    const unassignedPackages = packages.filter((p) => p.location == null)
    const groups: PackageNodeGroup[] = []

    if (assignedPackages.length > 0) {
      CollectionUtil.groupByKey(
        assignedPackages,
        (it) => (it as any)['location']['storagePath']
      ).forEach((nodePackages: Package[], storagePath: string) =>
        groups.push(this.buildGroup(StoragePath.parse(storagePath), nodePackages))
      )
    }

    if (unassignedPackages.length > 0) {
      groups.push(this.buildGroup(StoragePath.of([]), unassignedPackages))
    }

    return groups
  }

  private buildGroup(storagePath: StoragePath, packages: Package[]): PackageNodeGroup {
    return new PackageNodeGroup(storagePath, packages)
  }
}

export class PackageNodeGroup {
  constructor(
    public storagePath: StoragePath,
    public packages: Package[]
  ) {}
}
